import { DefaultContext } from "@apollo/client";
import { useMemo } from "react";
import { useCurrentBrandData, useCurrentBrandId } from "utils/currentUserHooks";
import { ADMIN_CONTEXT } from "./admin";
import { addActiveBrandIdToContext } from "./brand";

const EMPTY_CONTEXT = {};

type AdminOrBrandContextReturnTuple = [
  context: DefaultContext | undefined,
  isReady: boolean,
];

export function getAdminOrBrandContext(
  isAdmin: boolean | undefined,
  brandId: UUID | null | undefined,
  context: DefaultContext = EMPTY_CONTEXT,
): AdminOrBrandContextReturnTuple {
  if (isAdmin) {
    return [{ ...context, ...ADMIN_CONTEXT }, true];
  }
  return [addActiveBrandIdToContext(brandId, context), !!brandId];
}

type Options = {
  context?: DefaultContext;
  isDisabledForParentBrand?: boolean;
};

export function useAdminOrBrandContext(
  isAdmin: boolean | undefined,
  { context = EMPTY_CONTEXT, isDisabledForParentBrand }: Options = {},
) {
  const brandId = useCurrentBrandId();

  const brandData = useCurrentBrandData();

  const { id: brandDataId, isParent } = brandData || {};

  return useMemo<AdminOrBrandContextReturnTuple>(() => {
    if (!isAdmin) {
      if (!isDisabledForParentBrand && brandId !== brandDataId) {
        return [context, false];
      }

      if (isDisabledForParentBrand) {
        if (typeof isParent !== "boolean") return [context, false];
        if (isParent) return [context, true];
      }
    }

    return getAdminOrBrandContext(isAdmin, brandId, context);
  }, [
    isAdmin,
    isDisabledForParentBrand,
    isParent,
    brandId,
    brandDataId,
    context,
  ]);
}
